import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { FiShoppingCart, FiZap, FiMapPin, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './SecondHandCard.css';

const NPR = (amount) =>
  'NPR ' + parseFloat(amount || 0).toLocaleString('en-NP', { minimumFractionDigits: 0 });

export const CONDITION_LABELS = {
  like_new: 'Like New',
  excellent: 'Excellent',
  good: 'Good',
  fair: 'Fair',
};

export const STATUS_META = {
  pending: { label: 'Awaiting approval', badge: 'badge-yellow', icon: <FiClock /> },
  approved: { label: 'Live', badge: 'badge-green', icon: <FiCheckCircle /> },
  rejected: { label: 'Rejected', badge: 'badge-red', icon: <FiXCircle /> },
};

/**
 * Card for a second-hand listing.
 * `showStatus` turns on the moderation badge — used on "My Listings" where a
 * seller sees their own pending/rejected items.
 */
const SecondHandCard = ({ item, showStatus = false }) => {
  const { addSecondHandItem } = useCart();

  const img = item.images?.find((i) => i.is_primary) || item.images?.[0];
  const isLive = item.status === 'approved' && !item.is_sold;
  const discount =
    item.original_price && parseFloat(item.original_price) > parseFloat(item.price)
      ? Math.round((1 - parseFloat(item.price) / parseFloat(item.original_price)) * 100)
      : null;
  const status = STATUS_META[item.status];

  const handleAddToCart = async (e) => {
    e.preventDefault();
    try {
      await addSecondHandItem(item);
      toast.success(`${item.title} added to cart`);
    } catch (err) {
      toast.error(err.response?.data?.[0] || 'Failed to add item');
    }
  };

  return (
    <Link to={`/second-hand/${item.slug}`} className="sh-card">
      <div className="sh-image-wrap">
        {img ? (
          <img src={img.image} alt={img.alt || item.title} className="sh-image" loading="lazy" />
        ) : (
          <div className="sh-no-image"><FiZap /></div>
        )}
        <div className="sh-condition-tag">{item.condition_display || CONDITION_LABELS[item.condition]}</div>
        {discount && <div className="sh-discount">-{discount}%</div>}
        {item.is_sold && <div className="sh-sold">Sold</div>}
        {showStatus && status && !item.is_sold && (
          <div className={`sh-status badge ${status.badge}`}>{status.icon} {status.label}</div>
        )}
      </div>

      <div className="sh-body">
        <span className="sh-used-tag">Pre-owned</span>
        <h3 className="sh-title">{item.title}</h3>

        <div className="sh-meta">
          {item.location && <span><FiMapPin /> {item.location}</span>}
          {item.seller?.display_name && <span>by {item.seller.display_name}</span>}
        </div>

        <div className="sh-footer">
          <div className="sh-price-wrap">
            <div className="sh-price">{NPR(item.price)}</div>
            {item.original_price && (
              <div className="sh-original-price">{NPR(item.original_price)}</div>
            )}
          </div>
          <button
            className="sh-cart-btn"
            onClick={handleAddToCart}
            disabled={!isLive}
            title={isLive ? 'Add to cart' : 'Not available'}
          >
            <FiShoppingCart />
          </button>
        </div>
      </div>
    </Link>
  );
};

export default SecondHandCard;
